import { useEffect, useState } from 'react';
import { AlertTriangle, Ban, ExternalLink, Loader2, ShieldCheck } from 'lucide-react';
import { customerDuplicatesApi } from '../../services/customerDuplicatesApi';
import { formatDate } from '../../utils/format';
import type { GroupDetail, GroupMemberRow, GroupTotals } from '../../types/customerDuplicates';
import { COMPANY_LABELS, REASON_LABELS, STATUS_LABELS, ruleLabels } from '../../types/customerDuplicates';

function eur(n: number): string {
    return Number(n ?? 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * One customer identity, account by account.
 *
 * This table is the answer to the complaint that started the whole plan: revenue, open items,
 * overdue exposure, dunning level, blocked status and last movement PER COMPANY, and a total row.
 * The total is `detail.totals`, summed by the API from these very rows — and `detail.rollup` is
 * the database's own aggregate of the same set. They must agree; when they do not, the panel says
 * so out loud rather than showing one of them and hoping.
 *
 * Every BIT-valued flag (`isBlocked`, `isProblemBlocked`, …) arrives as a NUMBER from the mssql
 * driver, so each is read through `Boolean(...)` or `> 0` — a bare `&&` renders a falsy 0 as a
 * visible "0" (handoff P3 § 4.1).
 */
export function GroupDetailPanel({ groupId }: { groupId: number }) {
    const [detail, setDetail] = useState<GroupDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        customerDuplicatesApi.groupDetail(groupId)
            .then(d => { if (!cancelled) setDetail(d); })
            .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the group'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [groupId]);

    if (loading) {
        return (
            <div className="flex h-24 items-center justify-center border-y border-gray-100 bg-gray-50">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            </div>
        );
    }
    if (error || !detail) {
        return (
            <div className="border-y border-gray-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error ?? 'The group could not be loaded.'}
            </div>
        );
    }

    const { group, members, companyRoll, totals, rollup, evidence, decisions, suggestions, hubspot, config } = detail;
    const mismatch = Math.abs(Number(totals.openAmountEur) - Number(rollup.openAmountEur)) > 0.005
        || Math.abs(Number(totals.revenueEur) - Number(rollup.revenueEur)) > 0.005;

    const problemCompanies = companyRoll.filter(c => c.hasBlocked > 0 || c.hasOverdue > 0).map(c => c.companyCode);
    const clearCompanies = companyRoll.filter(c => c.hasClear > 0).map(c => c.companyCode);
    const warns = Boolean(group.blockedSplit) || Boolean(group.overdueSplit);

    return (
        <div className="space-y-4 border-y border-gray-100 bg-gray-50 px-4 py-4">
            {/* --- the warning, spelled out with the company names ---------------------------- */}
            {warns && (
                <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div>
                        <span className="font-medium">The companies do not agree about this customer.</span>{' '}
                        {problemCompanies.length > 0 && (
                            <>
                                <span className="uppercase">{problemCompanies.join(', ')}</span>{' '}
                                {Boolean(group.blockedSplit) && Boolean(group.overdueSplit)
                                    ? `carr${problemCompanies.length === 1 ? 'ies' : 'y'} it blocked or more than ${config.overdueDays} days overdue`
                                    : Boolean(group.blockedSplit)
                                        ? `ha${problemCompanies.length === 1 ? 's' : 've'} the account blocked`
                                        : `hold${problemCompanies.length === 1 ? 's' : ''} items more than ${config.overdueDays} days overdue`}
                                {clearCompanies.length > 0 && (
                                    <>
                                        , while <span className="uppercase">{clearCompanies.join(', ')}</span>{' '}
                                        carr{clearCompanies.length === 1 ? 'ies' : 'y'} it open and able to trade.
                                    </>
                                )}
                            </>
                        )}
                        <div className="mt-0.5 text-xs text-amber-800">
                            No mail is sent — this round shows the split on screen only (masterplan § 2).
                        </div>
                    </div>
                </div>
            )}

            {mismatch && (
                <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                    The total row and the database aggregate disagree ({eur(totals.openAmountEur)} vs
                    {' '}{eur(rollup.openAmountEur)} EUR open). Do not use either figure until this is explained.
                </div>
            )}

            {/* --- the accounts, per company, plus the total --------------------------------- */}
            <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    This customer exists in {group.companyCount} compan{group.companyCount === 1 ? 'y' : 'ies'},
                    on {group.memberCount} account{group.memberCount === 1 ? '' : 's'}
                </h4>
                <div className="overflow-x-auto rounded border border-gray-200 bg-white">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
                                <th className="px-2 py-1.5 text-left">Company</th>
                                <th className="px-2 py-1.5 text-left">Account</th>
                                <th className="px-2 py-1.5 text-left">Status</th>
                                <th className="px-2 py-1.5 text-right">Revenue ({config.revenueWindowMonths}m)</th>
                                <th className="px-2 py-1.5 text-right">Open items</th>
                                <th className="px-2 py-1.5 text-right">Open EUR</th>
                                <th className="px-2 py-1.5 text-right">Overdue EUR</th>
                                <th className="px-2 py-1.5 text-right">Max days</th>
                                <th className="px-2 py-1.5 text-center">Dunning</th>
                                <th className="px-2 py-1.5 text-left">Last mvmt</th>
                                <th className="px-2 py-1.5 text-left">Joined by</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map(m => <MemberRow key={m.customerId} m={m} overdueDays={config.overdueDays} />)}
                        </tbody>
                        <tfoot>
                            <TotalRow totals={totals} companyCount={group.companyCount} memberCount={group.memberCount} />
                        </tfoot>
                    </table>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                    The total row is the sum of the rows above it, computed from the same query — it is the
                    group-wide balance that no single company's view can show.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* --- why these accounts are one customer ---------------------------------- */}
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Why these are one customer
                    </h4>
                    {evidence.length === 0 ? (
                        <p className="text-xs text-gray-400">No current candidate pair — the group rests on decisions alone.</p>
                    ) : (
                        <ul className="space-y-1 text-xs">
                            {evidence.slice(0, 12).map(e => (
                                <li key={e.pairKey} className="flex items-baseline justify-between gap-2">
                                    <span className="shrink-0 font-mono text-gray-500" title={e.pairKey}>{e.pairKey}</span>
                                    <span className="min-w-0 text-right text-gray-700" title={e.matchedRules}>
                                        {ruleLabels(e.matchedRules).join(', ')} · {REASON_LABELS[e.primaryReason]}
                                    </span>
                                </li>
                            ))}
                            {evidence.length > 12 && (
                                <li className="text-gray-400">+{evidence.length - 12} more pairs</li>
                            )}
                        </ul>
                    )}
                    {decisions.length > 0 && (
                        <>
                            <h5 className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Human decisions touching this group
                            </h5>
                            <ul className="space-y-1 text-xs">
                                {decisions.map(d => (
                                    <li key={d.pairKey} className="flex items-baseline justify-between gap-2">
                                        <span className="truncate font-mono text-gray-500" title={d.pairKey}>{d.pairKey}</span>
                                        <span className={`shrink-0 ${d.status === 'NOT_DUPLICATE' ? 'text-amber-700' : 'text-green-700'}`}>
                                            {STATUS_LABELS[d.status]}
                                            {d.status === 'NOT_DUPLICATE' && ' — kept apart, also over a detour'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>

                {/* --- HubSpot, read only --------------------------------------------------- */}
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        HubSpot records <ShieldCheck className="h-3.5 w-3.5 text-gray-400" />
                        <span className="font-normal normal-case text-gray-400">read only</span>
                    </h4>
                    {hubspot.length === 0 ? (
                        <p className="text-xs text-gray-400">No HubSpot company is linked to any of these accounts.</p>
                    ) : (
                        <>
                            <p className="mb-1 text-xs text-gray-500">
                                {hubspot.length} CRM record{hubspot.length === 1 ? '' : 's'} would be affected by a
                                clean-up. The ERP link is strictly 1:1, so every duplicate account carries its own
                                HubSpot company.
                            </p>
                            <ul className="space-y-0.5 text-xs">
                                {hubspot.map(h => (
                                    <li key={h.hubspotCompanyId} className="flex items-baseline justify-between gap-2">
                                        <span className="truncate text-gray-700" title={h.hubspotCompanyName ?? undefined}>
                                            <span className="font-mono text-gray-400">{h.customerId}</span>{' '}
                                            {h.hubspotCompanyName ?? '(no name in the replica)'}
                                        </span>
                                        {h.linkToCrm && (
                                            <a href={h.linkToCrm} target="_blank" rel="noopener noreferrer"
                                                className="inline-flex shrink-0 items-center gap-1 text-orange-600 hover:underline">
                                                CRM <ExternalLink className="h-3 w-3" />
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            </div>

            {/* --- suggestions: reaching out of the group ---------------------------------- */}
            {suggestions.length > 0 && (
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Possibly also the same customer ({suggestions.length})
                    </h4>
                    <p className="mb-1.5 text-xs text-gray-500">
                        These accounts match a member but did NOT join the group. A weak rule — same name, no
                        country conflict, or a shared mail domain — never forms a group on its own, because
                        chaining those transitively would weld unrelated firms together. Judge them here, and
                        confirm the pair on the Pairs tab if they belong.
                    </p>
                    <ul className="space-y-1 text-xs">
                        {suggestions.slice(0, 15).map(s => (
                            <li key={`${s.pairKey}-${s.outsideCustomerId}`} className="flex items-baseline justify-between gap-2">
                                <span className="truncate text-gray-700">
                                    <span className="font-mono text-gray-400">{s.outsideCustomerId}</span>{' '}
                                    {s.outsideCustomerName ?? '—'}
                                    <span className="ml-1 uppercase text-gray-400">{s.outsideCompanyCode}</span>
                                </span>
                                <span className="max-w-[60%] shrink-0 text-right text-gray-500" title={s.matchedRules}>
                                    {ruleLabels(s.matchedRules).join(', ')}
                                    {Number(s.matchingPairs) > 1 && (
                                        <span className="ml-1 text-gray-400" title="This account matches several members of the group; the strongest match is shown">
                                            ×{s.matchingPairs}
                                        </span>
                                    )}
                                    {Number(s.hasStrongRule) > 0
                                        ? <span className="ml-1 text-amber-700">strong rule — refused by a limit or a separator</span>
                                        : <span className="ml-1 text-gray-400">weak only</span>}
                                </span>
                            </li>
                        ))}
                        {suggestions.length > 15 && (
                            <li className="text-gray-400">+{suggestions.length - 15} more</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

function MemberRow({ m, overdueDays }: { m: GroupMemberRow; overdueDays: number }) {
    const blocked = Number(m.isBlocked) > 0;
    const problemOverdue = Number(m.isProblemOverdue) > 0;
    const active = Number(m.isActiveAccount) > 0;
    return (
        <tr className={`border-b border-gray-100 last:border-0 ${blocked || problemOverdue ? 'bg-amber-50/50' : ''}`}>
            <td className="px-2 py-1.5 font-medium uppercase text-gray-700" title={COMPANY_LABELS[m.companyCode]}>
                {m.companyCode}
            </td>
            <td className="px-2 py-1.5">
                <span className="font-mono text-xs text-gray-500">{m.customerCode}</span>{' '}
                <span className="text-gray-800">{m.customerName ?? '—'}</span>
                {Number(m.isPrimary) > 0 && (
                    <span className="ml-1 rounded bg-blue-50 px-1 py-0.5 text-[10px] text-blue-700" title="The account a merge would most plausibly keep — a suggestion, nothing is written to the ERP">
                        keep?
                    </span>
                )}
                {m.city && <div className="text-[10px] text-gray-400">{m.city}{m.countryISO3 ? `, ${m.countryISO3}` : ''}</div>}
            </td>
            <td className="px-2 py-1.5 text-xs">
                {blocked ? (
                    <span className="inline-flex items-center gap-1 text-red-600" title={m.blockedFor ?? 'Blocked in the ERP'}>
                        <Ban className="h-3 w-3" /> blocked
                    </span>
                ) : active ? (
                    <span className="text-green-700">open</span>
                ) : (
                    <span className="text-gray-400">dormant</span>
                )}
            </td>
            <td className="px-2 py-1.5 text-right text-gray-700">
                {Number(m.revenueEur) > 0 ? eur(m.revenueEur) : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-2 py-1.5 text-right text-gray-700">
                {m.openItems > 0 ? m.openItems : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-2 py-1.5 text-right text-gray-800">
                {Number(m.openAmountEur) > 0 ? eur(m.openAmountEur) : <span className="text-gray-300">—</span>}
            </td>
            <td className={`px-2 py-1.5 text-right ${problemOverdue ? 'font-medium text-amber-800' : 'text-gray-500'}`}>
                {Number(m.overdueAmountEur) > 0 ? eur(m.overdueAmountEur) : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-2 py-1.5 text-right text-xs text-gray-600" title={`Threshold ${overdueDays} days`}>
                {m.maxDaysOverdue > 0 ? m.maxDaysOverdue : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-2 py-1.5 text-center text-xs">
                {Number(m.dunningLevelSent) > 0
                    ? <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">L{m.dunningLevelSent}</span>
                    : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-2 py-1.5 text-xs text-gray-500">
                {m.lastMovement ? formatDate(m.lastMovement) : <span className="text-gray-300">never</span>}
            </td>
            <td className="px-2 py-1.5 text-[10px] text-gray-500" title={m.joinedByRule}>
                <span className="block max-w-[260px]">{ruleLabels(m.joinedByRule).join(', ')}</span>
            </td>
        </tr>
    );
}

function TotalRow({ totals, companyCount, memberCount }: {
    totals: GroupTotals; companyCount: number; memberCount: number;
}) {
    return (
        <tr className="border-t-2 border-gray-300 bg-gray-50 font-medium">
            <td className="px-2 py-1.5 text-gray-700" colSpan={2}>
                Total · {memberCount} account{memberCount === 1 ? '' : 's'} in {companyCount} compan{companyCount === 1 ? 'y' : 'ies'}
            </td>
            <td className="px-2 py-1.5 text-xs text-gray-500">
                {totals.blockedMembers > 0 ? `${totals.blockedMembers} blocked` : ''}
            </td>
            <td className="px-2 py-1.5 text-right text-gray-900">{eur(totals.revenueEur)}</td>
            <td className="px-2 py-1.5 text-right text-gray-900">{totals.openItems}</td>
            <td className="px-2 py-1.5 text-right text-gray-900">{eur(totals.openAmountEur)}</td>
            <td className="px-2 py-1.5 text-right text-gray-900">{eur(totals.overdueAmountEur)}</td>
            <td className="px-2 py-1.5 text-right text-gray-900">{totals.maxDaysOverdue || ''}</td>
            <td className="px-2 py-1.5 text-center text-xs text-gray-700">
                {totals.dunningLevelSent > 0 ? `L${totals.dunningLevelSent}` : ''}
            </td>
            <td className="px-2 py-1.5" colSpan={2} />
        </tr>
    );
}
